import { faClose, faEnvelope, faLock, faMotorcycle, faPhone, faUser } from "@fortawesome/free-solid-svg-icons"
import Animate, { AnimationTypes } from "../../../components/Animate/Animate"
import Button, { ButtonIcon } from "../../../components/Button/Button"
import ModalPortal from "../../../components/Modal/ModalPortal"
import { TitlePageResponsiveIconSmall } from "../../../components/Titles/Titles"
import { SIZE } from "../../../configuration/Sizes"
import THEME from "../../../configuration/Theme"
import defaultStyle from "../../../styles/defaultPageStyle.module.css"
import InputField from "../../../components/InputField/InputField"
import { useCreateCourier } from "../../../hooks/Courier/useCreateCourier"
import { useSaveCourier } from "../../../hooks/Courier/useSaveCourier"
import { companyMemory } from "../../../Objects/Memory/Memory"
import { useEffect } from "react"
import { usePermission } from "../../../hooks/Permissions/usePermission"
import { Permissions } from "../../../Enum/PermissionsType"
import { PopupHelpButton } from "../../../components/Help/PopupHelpButton"
import { useTranslation } from "react-i18next"

interface CreateCourierPupopProps {
  onClonse: () => any
  onSuccess?: () => any
}

export function CreateCourierPupop({ onClonse, onSuccess }: CreateCourierPupopProps) {
  const { t } = useTranslation("frontend")

  const { handleTryCourierCreateAccountAsync } = useSaveCourier()

  const canEdite = !usePermission(Permissions.CanEditeCourier)

  const {
    name,
    phone,
    email,
    password,
    setName,
    setPhone,
    setEmail,
    setPassword,
    setLoading,
    setCompanyEntityId,
    handleSubmitForm,
    acceptedFinancialTerm,
    setAcceptedFinancialTerm,
  } = useCreateCourier()

  useEffect(() => {
    setCompanyEntityId(companyMemory.id)
  }, [])

  const handleSignUpSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    try {
      setLoading(true)

      const dto = handleSubmitForm()

      await handleTryCourierCreateAccountAsync(dto, () => {
        onSuccess && onSuccess()

        onClonse()
      })
    } finally {
      setLoading(false)
    }
  }

  return (
    <ModalPortal>
      <div className={defaultStyle.overlay}>
        <Animate type={AnimationTypes.slideUpIn} duration="0.3s">
          <div className={defaultStyle.alert1}>
            <TitlePageResponsiveIconSmall
              title={"Cadastrar Entregador"}
              icon={faMotorcycle}
              iconColor={THEME.colors.iconBGColor}
              children={
                <div className="flex items-center gap-2">
                  <PopupHelpButton
                    helpKey="pages.delivery.pupop.createcourierpupop.1"
                    variant="global"
                    title={t("helpPopup:global.deliveryCourierCreate.title")}
                    description={t("helpPopup:global.deliveryCourierCreate.description")}
                    bullets={t("helpPopup:global.deliveryCourierCreate.bullets", { returnObjects: true }) as string[]}
                  />

                  <ButtonIcon
                    sizeNumber={SIZE.buttonCloseIconSize}
                    bgColor={THEME.colors.buttonBGCloseColor}
                    iconColor={THEME.colors.buttonFGCloseColor}
                    icon={faClose}
                    onClick={onClonse}
                    size="small"
                    descriptionKey="interactionDescriptions:pages.delivery.pupop.createCourierPupop.button.onClonse"
                  />
                </div>
              }
            />

            <form onSubmit={handleSignUpSubmit} className="mt-2 space-y-4 p-1">
              <InputField
                required
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                icon={faUser}
                disable={canEdite}
                placeholder="Nome completo"
                descriptionKey="interactionDescriptions:pages.delivery.pupop.createCourierPupop.input.nomeCompleto"
              />

              <InputField
                required
                type="number"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                icon={faPhone}
                disable={canEdite}
                placeholder="Telefone"
                descriptionKey="interactionDescriptions:pages.delivery.pupop.createCourierPupop.input.telefone"
              />

              <InputField
                required
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                icon={faEnvelope}
                disable={canEdite}
                placeholder="E-mail"
                descriptionKey="interactionDescriptions:pages.delivery.pupop.createCourierPupop.input.eMail"
              />

              <InputField
                required
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                icon={faLock}
                disable={canEdite}
                placeholder="Senha inicial"
                descriptionKey="interactionDescriptions:pages.delivery.pupop.createCourierPupop.input.senhaInicial"
              />

              <Animate type={AnimationTypes.slideUpIn} duration="1.5s">
                <div className="rounded-md border border-orange-200 bg-orange-50 px-3 py-3 text-xs text-slate-700">
                  <p className="font-bold text-red-700">Aviso obrigatório sobre pagamentos</p>

                  <p className="mt-1">
                    O sistema não paga, não custodia e não intermedeia repasses para entregadores. Toda negociação e
                    pagamento são feitos diretamente entre restaurante e entregador.
                  </p>

                  <label className="mt-3 flex items-start gap-2 font-medium text-slate-800">
                    <input
                      disabled={canEdite}
                      type="checkbox"
                      checked={acceptedFinancialTerm}
                      onChange={(e) => setAcceptedFinancialTerm(e.target.checked)}
                      className="mt-0.5 h-4 w-4 accent-red-600"
                      data-interaction-description-key="interactionDescriptions:pages.delivery.pupop.createCourierPupop.input.input"
                    />
                    <span>Declaro que o entregador visualizou e concordou com este aviso.</span>
                  </label>
                </div>
              </Animate>

              <div className={defaultStyle.divisionBar}></div>

              <Button
                typeButton="submit"
                type="primary"
                size="medium"
                label={"Salvar e vincular"}
                disabled={canEdite}
                bgColor={THEME.colors.buttonBGSaveColor}
                descriptionKey="interactionDescriptions:pages.delivery.pupop.createCourierPupop.button.salvarEVincular"
              />
            </form>
          </div>
        </Animate>
      </div>
    </ModalPortal>
  )
}
